import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bettingService } from '../../services/bettingAPI';
import UserLayout from './UserLayout'; // <--- IMPORT THE LAYOUT
import '../../styles/user/CurrentBets.css';

const CurrentBets = () => {
  const navigate = useNavigate();
  const [bets, setBets] = useState([]);
  const [summary, setSummary] = useState({
    totalBets: 0,
    totalExposure: 0,
    totalStake: 0,
    totalPotentialWin: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedBet, setSelectedBet] = useState(null);
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    loadCurrentBets();
  }, [filter, sortBy]);

  const loadCurrentBets = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 100
      };
      
      if (filter !== 'all') {
        params.marketType = filter;
      }
      
      const [betsResponse, exposureResponse] = await Promise.all([
        bettingService.getCurrentBets(params),
        bettingService.getUserExposure()
      ]);
      
      console.log('Current bets response:', betsResponse);
      console.log('Exposure response:', exposureResponse);
      
      if (betsResponse && betsResponse.data) {
        let sortedBets = betsResponse.data.bets || [];
        
        switch(sortBy) {
          case 'newest':
            sortedBets = sortedBets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            break;
          case 'oldest':
            sortedBets = sortedBets.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            break;
          case 'highestStake':
            sortedBets = sortedBets.sort((a, b) => (b.stake || 0) - (a.stake || 0));
            break;
          case 'highestWin':
            sortedBets = sortedBets.sort((a, b) => (b.potentialWin || 0) - (a.potentialWin || 0));
            break;
          default:
            break;
        }
        
        setBets(sortedBets);
        
        let totalExposure = 0;
        if (exposureResponse && exposureResponse.data) {
          totalExposure = exposureResponse.data.totalExposure || 0;
        }
        
        setSummary({
          totalBets: betsResponse.data.summary?.totalBets || sortedBets.length,
          totalExposure: totalExposure,
          totalStake: betsResponse.data.summary?.totalStake || sortedBets.reduce((sum, b) => sum + (b.stake || 0), 0),
          totalPotentialWin: betsResponse.data.summary?.totalPotentialWin || sortedBets.reduce((sum, b) => sum + (b.potentialWin || 0), 0)
        });
      }
      setError(null);
    } catch (err) {
      console.error('Error loading current bets:', err);
      setError('Failed to load current bets');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  const getMarketIcon = (type) => {
    const icons = {
      bookmaker: '🎯',
      bookmaker_tied: '🔄',
      bookmaker2: '🎰',
      session: '📊',
      match: '🏏',
      tied: '⚖️'
    };
    return icons[type] || '🎲';
  };

  const getMarketTypeColor = (type) => {
    const colors = {
      bookmaker: '#4361ee',
      bookmaker_tied: '#7209b7',
      bookmaker2: '#f72585',
      session: '#f9c74f',
      match: '#43aa8b',
      tied: '#e63946'
    };
    return colors[type] || '#6c757d';
  };

  const getBetTypeStyle = (type) => {
    const styles = {
      back: { bg: '#d4edda', color: '#155724', label: 'BACK' },
      lay: { bg: '#f8d7da', color: '#721c24', label: 'LAY' },
      yes: { bg: '#cce5ff', color: '#004085', label: 'YES' },
      no: { bg: '#fff3cd', color: '#856404', label: 'NO' }
    };
    return styles[type] || { bg: '#e2e3e5', color: '#383d41', label: type?.toUpperCase() || 'BET' };
  };

  const getWinLossStatus = (bet) => {
    if (bet.status === 'won') return { text: 'WON', class: 'status-won' };
    if (bet.status === 'lost') return { text: 'LOST', class: 'status-lost' };
    if (bet.settlementStatus === 'settled') {
      return bet.result === 'win' ? { text: 'WON', class: 'status-won' } : { text: 'LOST', class: 'status-lost' };
    }
    return { text: 'ACTIVE', class: 'status-active' };
  };

  const calculateProfitLoss = (bet) => {
    if (bet.status === 'won' || (bet.settlementStatus === 'settled' && bet.result === 'win')) {
      return { amount: bet.potentialWin - bet.stake, class: 'profit' };
    }
    if (bet.status === 'lost' || (bet.settlementStatus === 'settled' && bet.result === 'loss')) {
      return { amount: -bet.exposureAmount, class: 'loss' };
    }
    return null;
  };

  const handleViewDetails = (bet) => {
    setSelectedBet(bet);
  };

  const closeDetails = () => {
    setSelectedBet(null);
  };

  return (
    <UserLayout activeTab="CurrentBets">
      
      {/* Main Content Area */}
      <div className="current-bets-page" style={{ width: '100%' }}>

        <div className="bets-controls">
          <div className="bets-controls__tabs">
            <button 
              className={`bets-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>

            <button 
              className={`bets-tab ${filter === 'bookmaker' ? 'active' : ''}`}
              onClick={() => setFilter('bookmaker')}
            >
              Book
            </button>

            <button 
              className={`bets-tab ${filter === 'session' ? 'active' : ''}`}
              onClick={() => setFilter('session')}
            >
              Session
            </button>

            <button 
              className={`bets-tab ${filter === 'bookmaker_tied' ? 'active' : ''}`}
              onClick={() => setFilter('bookmaker_tied')}
            >
              Tied
            </button>
          </div>

          <div className="bets-controls__sort">
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="highestStake">Stake</option>
              <option value="highestWin">Win</option>
            </select>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            {error}
            <button onClick={loadCurrentBets} className="retry-btn">Retry</button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading your bets...</p>
          </div>
        ) : (
          <>
            {/* Bets List */}
            {bets.length === 0 ? (
              <div className="bets-empty">
              
              </div>
            ) : (
              <div className="bets-table-wrapper">
                <table className="bets-table">
                  <thead>
                    <tr>
                      <th>Match</th>
                      <th>Market</th>
                      <th>Selection</th>
                      <th>Type</th>
                      <th>Odds</th>
                      <th>Stake</th>
                      <th>Exposure</th>
                      <th>Win</th>
                      <th>Status</th>
                      <th>Time</th>
                      <th></th>
                    </tr>
                  </thead>

                  <tbody>
                    {bets.map((bet) => {
                      const betTypeStyle = getBetTypeStyle(bet.betType);
                      const winLossStatus = getWinLossStatus(bet);
                      const profitLoss = calculateProfitLoss(bet);

                      return (
                        <tr key={bet._id || bet.betId}>
                          {/* Match Name */}
                          <td className="match-cell">
                            <span className="match-name-badge">
                              🏏 {bet.matchName || 'N/A'}
                            </span>
                          </td>

                          {/* Market */}
                          <td className="market-cell">
                            <div>{bet.marketName}</div>
                          </td>

                          {/* Selection */}
                          <td>{bet.selectionName}</td>

                          {/* Type */}
                          <td>
                            <span 
                              className="bet-type"
                              style={{ background: betTypeStyle.bg, color: betTypeStyle.color }}
                            >
                              {betTypeStyle.label}
                            </span>
                          </td>

                          {/* Odds */}
                          <td>{bet.odds}</td>

                          {/* Stake */}
                          <td>{formatCurrency(bet.stake)}</td>

                          {/* Exposure */}
                          <td className="neg">{formatCurrency(bet.exposureAmount)}</td>

                          {/* Win */}
                          <td className="pos">{formatCurrency(bet.potentialWin)}</td>

                          {/* Status */}
                          <td>
                            <span className={`status ${winLossStatus.class}`}>
                              {winLossStatus.text}
                            </span>

                            {profitLoss && (
                              <div className={`pl ${profitLoss.class}`}>
                                {profitLoss.amount > 0 ? '+' : ''}
                                {formatCurrency(profitLoss.amount)}
                              </div>
                            )}
                          </td>

                          {/* Time */}
                          <td className="time-cell">
                            {formatDate(bet.createdAt)}
                          </td>

                          {/* Action */}
                          <td>
                            <button 
                              className="view-btn"
                              onClick={() => handleViewDetails(bet)}
                            >
                              →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Details Modal */}
        {selectedBet && (
          <div className="bet-modal-overlay" onClick={closeDetails}>
            <div className="bet-modal" onClick={e => e.stopPropagation()}>
              <div className="bet-modal__header">
                <h3 className="bet-modal__title">Bet Details</h3>
                <button className="bet-modal__close" onClick={closeDetails}>×</button>
              </div>

              <div className="bet-modal__body">
                <div className="bet-modal__section">
                  <div className="bet-modal__row">
                    <span className="bet-modal__label">Bet ID</span>
                    <span className="bet-modal__value bet-modal__id">{selectedBet.betId}</span>
                  </div>

                  <div className="bet-modal__row">
                    <span className="bet-modal__label">Status</span>
                    <span className={`bet-modal__status ${getWinLossStatus(selectedBet).class}`}>
                      {getWinLossStatus(selectedBet).text}
                    </span>
                  </div>
                </div>

                {/* Match Name Section */}
                <div className="bet-modal__section">
                  <div className="bet-modal__grid">
                    <div className="bet-modal__item bet-modal__item--full">
                      <span className="bet-modal__label">Match</span>
                      <span className="bet-modal__value bet-modal__match">
                        🏏 {selectedBet.matchName || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bet-modal__section">
                  <div className="bet-modal__grid">
                    <div className="bet-modal__item">
                      <span className="bet-modal__label">Market</span>
                      <span className="bet-modal__value">{selectedBet.marketName}</span>
                    </div>

                    <div className="bet-modal__item">
                      <span className="bet-modal__label">Type</span>
                      <span 
                        className="bet-modal__value"
                        style={{ color: getMarketTypeColor(selectedBet.marketType) }}
                      >
                        {getMarketIcon(selectedBet.marketType)} {selectedBet.marketType?.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bet-modal__section">
                  <div className="bet-modal__grid">
                    <div className="bet-modal__item">
                      <span className="bet-modal__label">Selection</span>
                      <span className="bet-modal__value">{selectedBet.selectionName}</span>
                    </div>

                    <div className="bet-modal__item">
                      <span className="bet-modal__label">Bet Type</span>
                      <span 
                        className="bet-modal__chip"
                        style={{
                          backgroundColor: getBetTypeStyle(selectedBet.betType).bg,
                          color: getBetTypeStyle(selectedBet.betType).color
                        }}
                      >
                        {getBetTypeStyle(selectedBet.betType).label}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bet-modal__section">
                  <div className="bet-modal__grid">
                    <div className="bet-modal__item">
                      <span className="bet-modal__label">Odds</span>
                      <span className="bet-modal__value bet-modal__highlight">{selectedBet.odds}</span>
                    </div>

                    {selectedBet.targetValue && (
                      <div className="bet-modal__item">
                        <span className="bet-modal__label">Target</span>
                        <span className="bet-modal__value">
                          {selectedBet.comparison === 'above' ? 'Above' : 'Below'} {selectedBet.targetValue}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bet-modal__section">
                  <div className="bet-modal__grid">
                    <div className="bet-modal__item">
                      <span className="bet-modal__label">Stake</span>
                      <span className="bet-modal__value">{formatCurrency(selectedBet.stake)}</span>
                    </div>

                    <div className="bet-modal__item">
                      <span className="bet-modal__label">Exposure</span>
                      <span className="bet-modal__value bet-modal__neg">
                        {formatCurrency(selectedBet.exposureAmount)}
                      </span>
                    </div>

                    <div className="bet-modal__item">
                      <span className="bet-modal__label">Win</span>
                      <span className="bet-modal__value bet-modal__pos">
                        {formatCurrency(selectedBet.potentialWin)}
                      </span>
                    </div>

                    {selectedBet.liability > 0 && (
                      <div className="bet-modal__item">
                        <span className="bet-modal__label">Liability</span>
                        <span className="bet-modal__value bet-modal__neg">
                          {formatCurrency(selectedBet.liability)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bet-modal__section">
                  <div className="bet-modal__grid">
                    <div className="bet-modal__item">
                      <span className="bet-modal__label">Placed</span>
                      <span className="bet-modal__value">
                        {new Date(selectedBet.createdAt).toLocaleString('en-IN')}
                      </span>
                    </div>

                    {selectedBet.matchedAt && (
                      <div className="bet-modal__item">
                        <span className="bet-modal__label">Matched</span>
                        <span className="bet-modal__value">
                          {new Date(selectedBet.matchedAt).toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bet-modal__footer">
                <button className="bet-modal__btn" onClick={closeDetails}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </UserLayout>
  );
};

export default CurrentBets;